/**
 * components/WorkerHero.js — /me (Миний ХЭМАБ) хуудасны толгойд харагдах
 * ӨЭА даалгавар авсан ажилтны hero хэсэг.
 *
 * Загвар: дэлгэцийг 2 баганад хувааж зүүн талд цуваа товч, баруун талд ажлын
 * нарийвчилсан мэдээллийг харуулдаг "command center" хэв маяг.
 *
 *   ┌──────────────────────────┬───────────────────────────┐
 *   │ [15А/5А асуумж бөглөх →] │ Тавтай морил, <нэр>      │
 *   │ [⚠ Аюул мэдээлэх      →] │ Өнөөдрийн ажлын мэдээлэл │
 *   │                          │  ─ цех / хэсэг            │
 *   │                          │  ─ хаашаа явсан           │
 *   │                          │  ─ ажлын тайлбар         │
 *   └──────────────────────────┴───────────────────────────┘
 *
 * Гар утсан дээр баганад нэгдэж дээш доош цуваагдана.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "src/contexts/userContext";
// ⚠ PreworkBanner мөн ижил өгөгдөл хэрэглэдэг тул кэштэй hook-оор татна —
// хоёулаа НЭГ хүсэлт хуваалцана.
import { fetchMyOea } from "src/hooks/useMyOeaToday";
// ⚠ ЗӨВХӨН `todayStr` — огнооны туслах. Ажлын даалгаврын бүртгэлийг
//   localStorage-аас уншдаг `findByTn` ХАСАГДСАН (доорх useEffect-ийн
//   тайлбарыг үз).
import { todayStr } from "src/pages/naryad/register/naryadStore";
// Минутын мэдлэг модулийн brand өнгө — ГАНЦ эх сурвалж. Өмнө нь энд ягаан
// (#7C3AED) гэж хатуу бичигдсэн байсан нь /knowledge хуудсууд болон /me-гийн
// бусад товчны цэнхэр (#0EA5E9)-тэй зөрчилдөж байв.
import { KNOWLEDGE } from "src/pages/knowledge/theme";

const RED = "#E2382E";
const GREEN = "#16A34A";
const DARK = "#2C2C2A";
const MUTED = "#6B7280";

export default function WorkerHero({ onHasTaskChange }) {
  const navigate = useNavigate();
  const { user } = useUserContext();
  const [oea, setOea] = useState(null);
  const [naryadReg, setNaryadReg] = useState(null); // { worker, job } | null
  const [loading, setLoading] = useState(true);

  const myTn = String(user?.info?.tn || user?.tn || "");

  useEffect(() => {
    let mounted = true;
    if (!myTn) {
      setLoading(false);
      return;
    }
    // Параллель — ӨЭА болон Наряд хоёр эх сурвалжаас шалгана.
    // ⚠ ЗӨВХӨН ӨНӨӨДӨР (тухайн өдөр) томилогдсон ажлыг шалгана — гар утасны
    // апп шиг. Огноогүй бол хуучин/ирээдүйн бүртгэл ч гарч ирдэг байсан.
    // ⚠ НЭГ ЭХ СУРВАЛЖ. ӨЭА ба ажлын даалгаврын ажилтан ХОЁУЛАА
    //   `oea_worker_registrations` хүснэгтэд бичигддэг — ялгаа нь
    //   `work_type` ("OEA" | "NARYAD"). Өмнө нь наряд талыг
    //   localStorage-аас (`findNaryadByTn`) уншдаг байсан тул өөр
    //   төхөөрөмжөөс бүртгүүлсэн ажилтан «даалгаваргүй» харагдаж,
    //   5А бөглөх мөр нь огт гардаггүй байв.
    const today = todayStr();
    Promise.all([fetchMyOea(myTn, today)])
      .then(([oeaResult]) => {
        if (!mounted) return;
        const isNaryad =
          String(oeaResult?.work_type || "").toUpperCase() === "NARYAD";
        setOea(isNaryad ? null : oeaResult || null);
        // Наряд бол `worker` хэлбэрт оруулна — доорх код тэр бүтэцтэй
        setNaryadReg(isNaryad ? { worker: oeaResult, job: oeaResult } : null);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [myTn]);

  // Даалгавартай эсэхийг эцэг компонент (me/index.js) руу мэдэгдэнэ — ингэснээр
  // энгийн ажилтан (даалгаваргүй) дээр л бие даасан "Аюул мэдээлэх" товч
  // харагдана, даалгавартай хүнд hero доторх товчтой давхцахгүй.
  useEffect(() => {
    if (loading) return;
    onHasTaskChange?.(!!oea || !!naryadReg?.worker);
  }, [loading, oea, naryadReg, onHasTaskChange]);

  if (loading) return null;

  // Хэрэв ҮНДСЭН эх сурвалжаас аль нь ч олдсонгүй бол hero нуугдана.
  // Тэгэхгүй бол daалгaвaр-н хэлбэрээс шалтгаалан мэдээллийг бэлдэнэ.
  const hasOea = !!oea;
  const hasNaryad = !!naryadReg?.worker;
  if (!hasOea && !hasNaryad) return null;

  // Үндсэн "primary" даалгавар (header-т зориулсан мэдээлэл) — OEA → давамгай,
  // үгүй бол Naryad.
  const primaryFromOea = hasOea;
  const isNaryad = primaryFromOea
    ? oea.work_type === "NARYAD"
    : true; // naryadReg бол үргэлж 5A
  const targetPath = isNaryad ? "/highrisk/prework5a" : "/highrisk/prework15";
  const isCompleted = primaryFromOea
    ? isNaryad
      ? !!oea.checklist_5a_completed
      : !!oea.checklist_15_completed
    : !!naryadReg.worker.checklist_5a_completed;
  const checklistLabel = isNaryad ? "5А асуумж" : "15 асуумж";
  const checklistDesc = isNaryad
    ? "5А дадлын Pre-work шалгалт"
    : "Чухал хяналтын 15 асуумж";

  return (
    <div
      style={{
        background: "#F8F9FB",
        border: "1px solid #E6E9EF",
        borderRadius: 10,
        padding: 12,
        marginBottom: 10,
        boxShadow: "0 1px 6px rgba(20, 30, 50, 0.04)",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
          gap: 12,
        }}
        className="wh-grid"
      >
        {/* ── ЗҮҮН ТАЛ — товчнуудын баган ────────────────────────────── */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {/* Асуумж бөглөх товч — бөглөсөн ч ахин дарж хариуг харах боломжтой.
              isCompleted үед "Бөглөсөн / Харах" гэх ногоон төлвөөр харагдана,
              бөглөөгүй үед "Бөглөх" гэх улаан анхааруулга. */}
          <HeroButton
            primary
            onClick={() => navigate(targetPath)}
            iconClass="fas fa-clipboard-check"
            badge={
              isCompleted ? (
                <span
                  style={{
                    background: "rgba(255,255,255,0.25)",
                    color: "#fff",
                    borderRadius: 999,
                    padding: "2px 8px",
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: 0.4,
                  }}
                >
                  ✓ БӨГЛӨСӨН
                </span>
              ) : (
                <span
                  style={{
                    background: "rgba(255,255,255,0.25)",
                    color: "#fff",
                    borderRadius: 999,
                    padding: "2px 8px",
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: 0.4,
                  }}
                >
                  ⏳ ХҮЛЭЭГДЭЖ БУЙ
                </span>
              )
            }
            title={
              isCompleted
                ? `${checklistLabel} харах`
                : `${checklistLabel} бөглөх`
            }
            subtitle={
              isCompleted
                ? "Бөглөсөн хариултаа дахин харах боломжтой"
                : checklistDesc
            }
            accent={isCompleted ? GREEN : RED}
          />

          {/* Аюул мэдээлэх — улаан outline (15А primary улаан-аас ялгаатай) */}
          <HeroButton
            onClick={() => navigate("/hazard/register", {
              // Буцах товч /me-ийн «Аюулын бүртгэл» таб руу
              // (6-р таб) эргэж очихын тулд эх хаягаа дамжуулна.
              state: { from: "/me", tab: 6 },
            })}
            iconClass="fas fa-exclamation-triangle"
            title="Аюул мэдээлэх"
            subtitle="Ажлын байранд илрэх аюулыг ХАБ-д шуурхай мэдээлэх"
            accent={RED}
          />

          {/* Минутын мэдлэг — даалгавартай ажилтанд 3 дахь товч болж ЭНД
              (зүүн баганад, доор нь) орно. Даалгаваргүй үед /me хуудсан дээр
              бие даасан мөрөнд харагдана — 2 газар давхардахгүй. */}
          <HeroButton
            onClick={() => navigate("/knowledge/my")}
            iconClass="fas fa-play-circle"
            title="Минутын мэдлэг"
            subtitle="Өдөрт 1 минутын бичлэг, 1 асуулт · миний оноо"
            accent={KNOWLEDGE}
          />
        </div>

        {/* ── БАРУУН ТАЛ — мэдээллийн карт ───────────────────────────── */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #E6E9EF",
            borderRadius: 8,
            padding: 10,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: 100,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              marginBottom: 6,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: MUTED,
                letterSpacing: 0.6,
                textTransform: "uppercase",
              }}
            >
              <i
                className="fas fa-id-badge"
                style={{ marginRight: 6, color: RED }}
              />
              Өнөөдрийн даалгавар
            </div>
            {/* Бөглөсөн байдал — баруун дээд буланд тод харагдах status pill */}
            {isCompleted ? (
              <span
                style={{
                  background: GREEN,
                  color: "#fff",
                  borderRadius: 999,
                  padding: "3px 10px",
                  fontSize: 10.5,
                  fontWeight: 800,
                  letterSpacing: 0.4,
                  whiteSpace: "nowrap",
                }}
              >
                <i
                  className="fas fa-check-circle"
                  style={{ marginRight: 4 }}
                />
                {checklistLabel} БӨГЛӨСӨН
              </span>
            ) : (
              <span
                style={{
                  background: "#FEE2E1",
                  color: "#B5251D",
                  borderRadius: 999,
                  padding: "3px 10px",
                  fontSize: 10.5,
                  fontWeight: 800,
                  letterSpacing: 0.4,
                  whiteSpace: "nowrap",
                }}
              >
                <i className="fas fa-clock" style={{ marginRight: 4 }} />
                {checklistLabel} БӨГЛӨӨГҮЙ
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 800,
              color: DARK,
              marginBottom: 6,
              lineHeight: 1.2,
            }}
          >
            {isNaryad ? "Ажлын даалгаврын ажил" : "Өндөр Эрсдэлт Ажил"}
          </div>

          {(() => {
            // Info source — OEA эсвэл Naryad-аас ийн уншсан мэдээлэл.
            const src = primaryFromOea
              ? {
                  department_name: oea.department_name,
                  section_name: oea.section_name,
                  destination: oea.destination,
                  work_description: oea.work_description,
                }
              : {
                  department_name: naryadReg.job?.department_name,
                  section_name: naryadReg.job?.section_name,
                  destination: naryadReg.worker?.destination,
                  work_description:
                    naryadReg.job?.jobdesc || naryadReg.worker?.comment,
                };
            return (
              <>
                <Info
                  icon="fas fa-building"
                  label="Хэсэг"
                  value={src.department_name || "—"}
                />
                {src.section_name &&
                  src.section_name !== src.department_name && (
                    <Info
                      icon="fas fa-map-marker-alt"
                      label="Дэд нэгж"
                      value={src.section_name}
                    />
                  )}
                {src.destination && (
                  <Info
                    icon="fas fa-map-marker"
                    label="Хаашаа"
                    value={src.destination}
                  />
                )}
                {src.work_description && (
                  <Info
                    icon="fas fa-clipboard-list"
                    label="Ажил"
                    value={src.work_description}
                  />
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Гар утсан дээр 1 баганад нэгтгэх media query */}
      <style>{`
        @media (max-width: 768px) {
          .wh-grid { grid-template-columns: minmax(0, 1fr) !important; }
        }
      `}</style>
    </div>
  );
}

// ── Hero товч (том, гоё) ─────────────────────────────────────────────────
function HeroButton({
  iconClass,
  title,
  subtitle,
  accent,
  badge,
  onClick,
  disabled,
  primary,
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      style={{
        position: "relative",
        background: disabled
          ? "linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)"
          : primary
            ? `linear-gradient(135deg, ${accent} 0%, ${shade(accent, -10)} 100%)`
            : "#fff",
        color: disabled ? "#166534" : primary ? "#fff" : DARK,
        border: disabled
          ? "1px solid #BBF7D0"
          : primary
            ? "none"
            : `2px solid ${accent}`,
        borderRadius: 8,
        // Нимгэлэв: /me-гийн 3 товч дэлгэцийн дээд хэсгийг эзэлж,
        // доорх табууд харагдахгүй болдог байв.
        padding: "7px 10px",
        textAlign: "left",
        cursor: disabled ? "default" : "pointer",
        boxShadow: disabled
          ? "none"
          : primary
            ? `0 6px 20px ${hexAlpha(accent, 0.28)}`
            : `0 3px 12px ${hexAlpha(accent, 0.1)}`,
        transition: "transform 120ms ease, box-shadow 120ms ease",
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
      }}
      onMouseEnter={(e) => {
        if (!disabled)
          e.currentTarget.style.transform = "translateY(-2px) scale(1.01)";
      }}
      onMouseLeave={(e) => {
        if (!disabled) e.currentTarget.style.transform = "none";
      }}
    >
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: 6,
          background: disabled
            ? GREEN
            : primary
              ? "rgba(255,255,255,0.22)"
              : hexAlpha(accent, 0.14),
          color: disabled || primary ? "#fff" : accent,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          flexShrink: 0,
          border:
            disabled || primary ? "none" : `1px solid ${hexAlpha(accent, 0.3)}`,
        }}
      >
        <i className={iconClass} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              lineHeight: 1.2,
            }}
          >
            {title}
          </div>
          {badge}
        </div>
        <div
          style={{
            fontSize: 10,
            opacity: disabled ? 0.85 : primary ? 0.92 : 0.7,
            marginTop: 2,
            lineHeight: 1.3,
          }}
        >
          {subtitle}
        </div>
      </div>
      {!disabled && (
        <i
          className="fas fa-chevron-right"
          style={{
            fontSize: 12,
            opacity: primary ? 0.9 : 0.5,
            flexShrink: 0,
          }}
        />
      )}
    </button>
  );
}

// ── Баруун талын мэдээллийн мөр ─────────────────────────────────────────
function Info({ icon, label, value }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        padding: "4px 0",
        borderBottom: "1px solid #F1F2F4",
      }}
    >
      <div
        style={{
          width: 18,
          color: MUTED,
          fontSize: 11,
          flexShrink: 0,
          paddingTop: 1,
        }}
      >
        <i className={icon} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 9,
            color: MUTED,
            fontWeight: 600,
            letterSpacing: 0.3,
            textTransform: "uppercase",
            lineHeight: 1.2,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: DARK,
            fontWeight: 600,
            wordBreak: "break-word",
            lineHeight: 1.3,
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

// ── Энгийн өнгө аж helper-ууд ───────────────────────────────────────────
function shade(hex, percent) {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.max(0, Math.min(255, (num >> 16) + percent));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + percent));
  const b = Math.max(0, Math.min(255, (num & 0xff) + percent));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}
function hexAlpha(hex, a) {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}
